import dayjs from "dayjs";
import * as XLSX from "xlsx";

export const formatUTCDateToLocal = dateString => {
  if (!dateString) return "";

  // Convert UTC to local time
  const utcDate = new Date(dateString);
  const localDate = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);

  // Format date components
  const day = String(localDate.getDate()).padStart(2, "0");
  const month = String(localDate.getMonth() + 1).padStart(2, "0");
  const year = localDate.getFullYear();
  const hours = String(localDate.getHours()).padStart(2, "0");
  const minutes = String(localDate.getMinutes()).padStart(2, "0");

  // Calculate days difference
  const today = new Date();
  const timeDiff = today - localDate;
  const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

  return `${day}.${month}.${year} ${hours}:${minutes} (${daysDiff} days ago)`;
};

// Optional: Additional date formatting helpers
export const formatDateOnly = (dateString, isRequired = false) => {
  // if (!dateString) return "";
  // const date = new Date(dateString);
  // return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  const date = new Date(dateString);
  if (isNaN(date)) return ""; // 🚨 Handle invalid date input

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  const formattedDate = `${day}.${month}.${year}`;

  if (isRequired) {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${formattedDate} ${hours}:${minutes}`;
  }

  return formattedDate;
};

export const formatTimeOnly = dateString => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(
    2,
    "0"
  )}`;
};

export function commaToDot(input) {
  const str = String(input).trim();
  if (!str) return "";

  const hasComma = str.includes(",");
  const hasDot = str.includes(".");

  let calculableStr = str;

  // If a comma is present, it's the decimal separator. All dots are for thousands.
  // e.g., "1.500,36" -> "1500.36"
  if (hasComma) {
    calculableStr = str.replace(/\./g, "").replace(",", ".");
  }
  // If no comma, but dots are present, we need to decide.
  // e.g. "15.000" (fifteen thousand) vs "0.065" (zero point zero six five)
  else if (hasDot) {
    const parts = str.split(".");
    const lastPart = parts[parts.length - 1];

    // If the last part has 3 digits AND the number has more than 1 dot OR the integer part is > 0,
    // it's likely a thousands separator.
    if (lastPart.length === 3 && (parts.length > 2 || Number(parts[0]) > 0)) {
      calculableStr = str.replace(/\./g, "");
    }
    // Otherwise, the last dot is a decimal separator.
    // e.g., "0.065" -> "0.065"
    else {
      const integerPart = parts.slice(0, -1).join("");
      calculableStr = `${integerPart}.${lastPart}`;
    }
  }

  // Final validation to ensure it's a valid number
  if (isNaN(Number(calculableStr))) {
    // Before failing, check if it's a large number with comma separators the user intended as thousands
    const fallback = str.replace(/,/g, "");
    if (!isNaN(Number(fallback))) {
      return fallback;
    }
    return "";
  }

  return calculableStr;
}

export function dotToComma(input) {
  if (input === null || input === undefined || input === "") return "";

  const calculableValue = commaToDot(input);
  if (calculableValue === "") return "";
  const num = Number(calculableValue);
  if (isNaN(num)) return "";

  // Check if input has a decimal part
  const strValue = String(calculableValue);
  let decimalPart = "";
  let fractionDigits = 0;
  if (strValue.includes(".")) {
    decimalPart = strValue.split(".")[1] || "";
    fractionDigits = decimalPart.length;
  }

  // If there is a decimal part, preserve the number of decimals the user typed
  if (fractionDigits > 0) {
    return num.toLocaleString("de-DE", {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: 20,
    });
  } else {
    // Integer: show no decimals, but use thousand separator
    return num.toLocaleString("de-DE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
}
export function calculateProfitMargin(revenue, cost) {
  revenue = parseFloat(revenue) || 0;
  cost = parseFloat(cost) || 0;

  if (revenue === 0 && cost > 0) {
    return "-100.00";
  }

  if (isNaN(revenue) || revenue === 0) {
    return "0.00";
  }

  const profit = revenue - cost;
  const profitMargin = (profit / revenue) * 100;

  if (!isFinite(profitMargin)) {
    return "0.00";
  }

  return profitMargin.toFixed(2);
}

/**
 * Map your custom format IDs to actual format strings
 */
const dateFormatMap = {
  0: "YYYY/MM/DD",
  1: "DD.MM.YYYY", // default format
  2: "DD/MM/YYYY",
  3: "MMM/DD/YYYY",
  4: "DD/MMM/YYYY",
};

export const formatDate = (date, { formatId = "1", separator = ".", includeTime = false } = {}) => {
  if (!date) return "";

  // Handle cases where date part is "0000-00-00"
  if (date.startsWith("0000-00-00")) {
    if (includeTime) {
      const timePart = date.split(" ")[1] || "";
      return timePart ? `00.00.0000 | ${timePart.slice(0, 5)}` : "00.00.0000 | 00:00";
    } else {
      return "00.00.0000";
    }
  }

  const rawFormat = dateFormatMap[formatId] || "DD.MM.YYYY";
  let formatWithSeparator = rawFormat.replace(/\//g, separator);

  if (includeTime) {
    formatWithSeparator += " | HH:mm";
  }

  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return "";

  return dayjsDate.format(formatWithSeparator);
};

export function normalizeNumber(val) {
  const value = val?.toString();
  // Remove all dots
  let noDots = value?.replace(/\./g, "");
  // Replace comma with dot
  let normalized = noDots?.replace(",", ".");
  return normalized;
}
export function localizeNumber(val, maxDecimals = 3) {
  const num = Number(val);
  if (isNaN(num)) return val;
  return num.toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
}

export const isValidNumber = value => {
  return /^(?![^,]*,[^,]*,)(?!.*,[^,]*\.)[0-9.,]+$/.test(value) || value === "";
};

export const calculateDueDateFreelance = (inputDate, daysAfter) => {
  const noOfDays = daysAfter ? parseInt(daysAfter) : 30;
  const startDate = dayjs(inputDate).add(noOfDays, "day");

  const date = startDate.date();

  if (date <= 5) {
    return startDate.date(5);
  } else if (date <= 20) {
    return startDate.date(20);
  } else {
    return startDate.add(1, "month").date(5);
  }
};
export const openDefaultEmail = ({ to = "", subject = "", body = "" }) => {
  const params = new URLSearchParams();

  if (subject) params.append("subject", subject);
  if (body) params.append("body", body);

  const query = params.toString();
  const mailtoLink = query ? `mailto:${to}?${query}` : `mailto:${to}`;

  window.location.href = mailtoLink;
};
export const getRateFromPriceBasis__ = (priceBasis, rateType) => {
  if (!priceBasis) return "-";

  let parsed;
  try {
    parsed = typeof priceBasis === "string" ? JSON.parse(priceBasis) : priceBasis;
  } catch {
    return "-";
  }

  if (!Array.isArray(parsed)) return "-";

  let match;

  switch (rateType) {
    case "word":
      match = parsed.find(
        item =>
          item.basePriceUnit?.toLowerCase().includes("word") &&
          !item.basePriceUnit?.toLowerCase().includes("machine")
      );
      break;

    case "hour":
      match = parsed.find(
        item =>
          item.basePriceUnit?.toLowerCase().includes("hour") &&
          !item.basePriceUnit?.toLowerCase().includes("machine")
      );
      break;

    case "mte":
      match = parsed.find(item =>
        item.basePriceUnit?.toLowerCase().includes("machine translation editing")
      );
      break;

    // 👇 backward compatibility (Angular exact match)
    default:
      match = parsed.find(item => item.basePriceUnit === rateType);
  }

  if (!match?.basePrice) return "-";

  return dotToComma(match.basePrice);
};
export const getRateFromPriceBasis = (
  priceBasis,
  rateType,                 // "word" | "hour" | "mte"
  _priceLanguage,           // ignored (kept to avoid breaking calls)
  _sourceLang,
  _targetLang,
  selectedUnits = []
) => {
  if (!priceBasis) return "-";

  let parsed = [];

  try {
    parsed = typeof priceBasis === "string"
      ? JSON.parse(priceBasis)
      : priceBasis;
  } catch {
    return "-";
  }

  if (!Array.isArray(parsed)) return "-";

  /* ===================================================
     1️⃣ FILTER BY COLUMN TYPE (MOST IMPORTANT FIX)
     =================================================== */

  const unitFiltered = parsed.filter(item => {
    const unit = item.basePriceUnit?.toLowerCase() || "";

    if (rateType === "word") {
      return (
        unit.includes("words translation") &&
        !unit.includes("machine translation editing") &&
        !unit.includes("hour")
      );
    }

    if (rateType === "hour") {
      return unit.includes("hour");
    }

    if (rateType === "mte") {
      return unit.includes("machine translation editing");
    }

    return false;
  });

  if (!unitFiltered.length) return "-";

  /* ===================================================
     2️⃣ EXACT MATCH (ONLY INSIDE SAME UNIT TYPE)
     =================================================== */

  if (selectedUnits.length) {
    const exact = unitFiltered.find(item =>
      selectedUnits.includes(item.basePriceUnit)
    );

    if (exact?.basePrice) {
      return dotToComma(exact.basePrice);
    }

    // if exact match not for this column → show nothing
    return "-";
  }

  /* ===================================================
     3️⃣ FALLBACK LOGIC
     =================================================== */

  if (rateType === "word") {
    const values = unitFiltered
      .map(i => Number(i.basePrice))
      .filter(v => !isNaN(v));

    return values.length ? dotToComma(Math.max(...values)) : "-";
  }

  const first = unitFiltered.find(i => i.basePrice);

  return first?.basePrice ? dotToComma(first.basePrice) : "-";
};
export const formatLanguagesForExport = price_language => {
  if (!price_language) return "";

  // Case 1: already array
  if (Array.isArray(price_language)) {
    return price_language
      .map(l => l?.languagePrice)
      .filter(Boolean)
      .join(", ");
  }

  // Case 2: stringified JSON
  if (typeof price_language === "string") {
    try {
      const parsed = JSON.parse(price_language);
      if (Array.isArray(parsed)) {
        return parsed
          .map(l => l?.languagePrice)
          .filter(Boolean)
          .join(", ");
      }
      return parsed?.languagePrice || price_language;
    } catch {
      return price_language;
    }
  }

  // Case 3: object
  if (typeof price_language === "object") {
    return price_language.languagePrice || "";
  }

  return "";
};
export const handleExportExternalResourceExcel = (
  data,
  selectedResourceIds = [],
  filename = "External_Resources.xlsx"
) => {
  const filteredData =
    selectedResourceIds.length > 0
      ? data.filter(row => selectedResourceIds.includes(row.iUserId))
      : data;

  if (!filteredData || filteredData.length === 0) {
    alert("No data to export");
    return;
  }

  const exportData = filteredData.map((row, index) => ({
    "No.": index + 1,
    Resource: `${row.vFirstName || ""} ${row.vLastName || ""}`.trim(),
    "Rate / Word": dotToComma(parseFloat(row?.rate_word)),
    "Rate / Hour": dotToComma(parseFloat(row?.rate_hour)),
    "Rate MTE": dotToComma(parseFloat(row?.rate_mte)),
    // "Rate / Word": getRateFromPriceBasis(row.price_basis, "word"),
    // "Rate / Hour": getRateFromPriceBasis(row.price_basis, "hour"),
    // "Rate MTE": getRateFromPriceBasis(row.price_basis, "mte"),
    Currency: row.freelance_currency?.split(",")[0] || "EUR",
    Languages: formatLanguagesForExport(row.price_language),
    "Freelancer Type": row.freelancerType
      ? row.freelancerType.charAt(0).toUpperCase() + row.freelancerType.slice(1).toLowerCase()
      : "",
    Status: row.userStatus || "",
  }));

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "External Resources");

  XLSX.writeFile(workbook, filename);
};
export const capitalizeName = name => {
  if (!name) return "";
  return name
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
